import React from 'react';
import { Box, Typography, Chip, Stack, Divider, Tooltip } from '@mui/material';
import { useTheme, alpha, Theme } from '@mui/material/styles';
import { ChatCardWidget } from './types';
import { isKnownAnalysisWidget } from './widget-types';
import {
  DashboardOutlined,
  WarningOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FundOutlined,
  SearchOutlined,
  TeamOutlined,
} from '@ant-design/icons';

interface AiChatWidgetCardProps {
  widget: ChatCardWidget;
}

// React error boundary that catches render-time errors in a single widget so
// one malformed payload from the agent never crashes the whole chat card.
// Logs a warn with the offending widget for debugging and renders a small
// inline fallback in place of the widget.
interface WidgetErrorBoundaryProps {
  widget: ChatCardWidget;
  children: React.ReactNode;
}
interface WidgetErrorBoundaryState {
  error: Error | null;
}
class WidgetErrorBoundary extends React.Component<
  WidgetErrorBoundaryProps,
  WidgetErrorBoundaryState
> {
  constructor(props: WidgetErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error): WidgetErrorBoundaryState {
    return { error };
  }
  componentDidCatch(error: Error): void {
    // eslint-disable-next-line no-console
    console.warn(
      `[AiChat] widget render failed for type "${this.props.widget?.type}":`,
      error,
      this.props.widget,
    );
  }
  render() {
    if (!this.state.error) return this.props.children;
    return <WidgetFallback widget={this.props.widget} reason={this.state.error.message} />;
  }
}

const WidgetFallback = ({
  widget,
  reason,
}: {
  widget: ChatCardWidget;
  reason: string;
}) => {
  const theme = useTheme();
  const safeJson = (() => {
    try {
      return JSON.stringify(widget?.data ?? widget, null, 2);
    } catch {
      return '[unserializable widget data]';
    }
  })();
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        border: `1px dashed ${alpha(theme.palette.warning.main, 0.5)}`,
        bgcolor: alpha(theme.palette.warning.main, 0.04),
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: theme.palette.warning.main,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          fontSize: '0.7rem',
          mb: 0.5,
        }}
      >
        Widget could not be rendered
        {widget?.type ? ` · ${widget.type}` : ''}
      </Typography>
      <Typography
        variant="caption"
        sx={{ display: 'block', color: 'text.secondary', mb: 0.75, fontSize: '0.72rem' }}
      >
        {reason}
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1,
          borderRadius: 1,
          fontFamily: MONO,
          fontSize: '0.7rem',
          color: 'text.secondary',
          bgcolor: alpha(theme.palette.text.primary, 0.04),
          overflowX: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {safeJson}
      </Box>
    </Box>
  );
};

const MONO = 'SF Mono, Menlo, Consolas, monospace';
const NUMERIC_SX = { fontFamily: MONO, fontVariantNumeric: 'tabular-nums' as const };

const formatMoney = (value: number | null | undefined, compact = false): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (value === 0) return '$0';
  const abs = Math.abs(value);
  if (compact && abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (compact && abs >= 10_000) return `$${(value / 1_000).toFixed(1)}K`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(value);
};

const formatMoneyFull = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(value);
};

const formatPercent = (value: number | null | undefined, digits = 1): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value.toFixed(digits)}%`;
};

const formatCount = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '0';
  return value.toLocaleString('en-US');
};

const formatAccountAge = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const utilizationColor = (pct: number | null | undefined, theme: Theme): string => {
  if (pct === null || pct === undefined) return theme.palette.text.disabled;
  if (pct >= 60) return theme.palette.error.main;
  if (pct >= 30) return theme.palette.warning.main;
  return theme.palette.success.main;
};

const onTimeColor = (pct: number | null | undefined, theme: Theme): string => {
  if (pct === null || pct === undefined) return theme.palette.text.disabled;
  if (pct >= 97) return theme.palette.success.main;
  if (pct >= 90) return theme.palette.warning.main;
  return theme.palette.error.main;
};

const derogatoryColor = (count: number | null | undefined, theme: Theme): string => {
  if (!count || count <= 0) return theme.palette.success.main;
  if (count <= 2) return theme.palette.warning.main;
  return theme.palette.error.main;
};

const inquiryColor = (count: number | null | undefined, theme: Theme): string => {
  if (!count || count <= 1) return theme.palette.success.main;
  if (count <= 4) return theme.palette.warning.main;
  return theme.palette.error.main;
};

const scoreBandColor = (score: number | null | undefined, theme: Theme): string => {
  if (!score) return theme.palette.text.secondary;
  if (score >= 800) return theme.palette.success.dark;
  if (score >= 740) return theme.palette.success.main;
  if (score >= 670) return theme.palette.info.main;
  if (score >= 580) return theme.palette.warning.main;
  return theme.palette.error.main;
};

const scoreBandLabel = (score: number | null | undefined): string => {
  if (!score) return '';
  if (score >= 800) return 'Excellent';
  if (score >= 740) return 'Very Good';
  if (score >= 670) return 'Good';
  if (score >= 580) return 'Fair';
  return 'Poor';
};

const riskColorMap = (theme: Theme): Record<string, string> => ({
  low: theme.palette.success.main,
  medium: theme.palette.warning.main,
  high: theme.palette.error.light,
  very_high: theme.palette.error.main,
});

const WIDGET_CARD_SX = (borderColor: string, bgTint: string) => ({
  p: 1.5,
  borderRadius: 2,
  border: `1px solid ${borderColor}`,
  background: bgTint,
});

const SECTION_LABEL_SX = {
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: 'uppercase' as const,
  color: 'text.primary',
  opacity: 0.82,
};

const MICRO_LABEL_SX = {
  fontSize: '0.7rem',
  fontWeight: 600,
  letterSpacing: 0.7,
  textTransform: 'uppercase' as const,
  color: 'text.primary',
  opacity: 0.72,
};

const WidgetHeader = ({
  icon, title, badge, badgeColor,
}: { icon: React.ReactNode; title: string; badge?: string; badgeColor?: string }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.25}>
      <Stack direction="row" alignItems="center" spacing={0.85}>
        <Box sx={{ color: alpha(theme.palette.text.primary, 0.82), fontSize: 14, display: 'flex' }}>{icon}</Box>
        <Typography variant="caption" sx={{
          color: 'text.primary', opacity: 0.88, textTransform: 'uppercase',
          letterSpacing: 1, fontWeight: 700, fontSize: '0.8125rem',
        }}>
          {title}
        </Typography>
      </Stack>
      {badge && (
        <Chip label={badge} size="small" sx={{
          height: 20, fontSize: '0.7rem', fontWeight: 700,
          letterSpacing: 0.3,
          bgcolor: alpha(badgeColor || theme.palette.primary.main, 0.22),
          color: badgeColor || theme.palette.primary.main,
          border: `1px solid ${alpha(badgeColor || theme.palette.primary.main, 0.45)}`,
          '& .MuiChip-label': { px: 0.85, textTransform: 'capitalize' },
        }} />
      )}
    </Stack>
  );
};

const SegmentedBar = ({
  segments, height = 6, ariaLabel,
}: {
  segments: Array<{ value: number; color: string; label?: string }>;
  height?: number;
  ariaLabel?: string;
}) => {
  const theme = useTheme();
  const total = segments.reduce((sum, s) => sum + (s.value || 0), 0);
  if (total <= 0) {
    return (
      <Box
        aria-label={ariaLabel}
        sx={{
          height, borderRadius: height / 2,
          bgcolor: alpha(theme.palette.text.primary, 0.06),
        }}
      />
    );
  }
  return (
    <Stack
      direction="row"
      spacing={0.25}
      aria-label={ariaLabel}
      sx={{ height, borderRadius: height / 2, overflow: 'hidden' }}
    >
      {segments.map((seg, i) => {
        const pct = ((seg.value || 0) / total) * 100;
        if (pct <= 0) return null;
        const bar = (
          <Box
            key={i}
            sx={{
              width: `${pct}%`,
              bgcolor: seg.color,
              transition: 'width 400ms ease',
            }}
          />
        );
        return seg.label ? (
          <Tooltip key={i} title={`${seg.label}: ${seg.value}`} arrow placement="top">
            {bar}
          </Tooltip>
        ) : bar;
      })}
    </Stack>
  );
};

const ThresholdDot = ({ color }: { color: string }) => (
  <Box sx={{
    width: 6, height: 6, borderRadius: '50%',
    bgcolor: color,
    boxShadow: `0 0 0 3px ${alpha(color, 0.18)}`,
    flexShrink: 0,
  }} />
);

const ScoreCard = ({ data }: { data: Record<string, any> }) => {
  const theme = useTheme();
  const score: number | undefined = data.score;
  const providedColor: string | undefined = data.color;
  const bandColor = scoreBandColor(score, theme);
  const accent = providedColor || bandColor;
  const category: string | undefined = data.category || scoreBandLabel(score) || undefined;
  const modelName: string = data.modelName || 'Credit Score';
  const factors: Array<any> = Array.isArray(data.topFactors) ? data.topFactors : [];

  const min = 300;
  const max = 850;
  const clamped = score ? Math.max(min, Math.min(max, score)) : min;
  const pct = ((clamped - min) / (max - min)) * 100;
  const bands = [
    { from: 300, to: 579, color: theme.palette.error.main },
    { from: 580, to: 669, color: theme.palette.warning.main },
    { from: 670, to: 739, color: theme.palette.info.main },
    { from: 740, to: 799, color: theme.palette.success.main },
    { from: 800, to: 850, color: theme.palette.success.dark },
  ];

  return (
    <Box sx={WIDGET_CARD_SX(alpha(accent, 0.25), alpha(accent, 0.04))}>
      <WidgetHeader
        icon={<DashboardOutlined />}
        title={modelName}
        badge={category}
        badgeColor={accent}
      />

      <Stack direction="row" alignItems="baseline" spacing={1} mb={1}>
        <Typography sx={{
          ...NUMERIC_SX,
          fontSize: '2.25rem',
          fontWeight: 800,
          color: accent,
          lineHeight: 1,
          letterSpacing: -1.2,
        }}>
          {score ?? '—'}
        </Typography>
        <Stack spacing={0}>
          <Typography variant="caption" sx={{
            color: 'text.secondary',
            fontSize: '0.76rem',
            fontWeight: 600,
          }}>
            of {max}
          </Typography>
          {data.percentile && (
            <Typography variant="caption" sx={{ ...NUMERIC_SX, color: 'text.secondary', fontSize: '0.74rem' }}>
              {data.percentile}th pct
            </Typography>
          )}
        </Stack>
      </Stack>

      <Box
        role="progressbar"
        aria-label={`Credit score ${score} of ${max}`}
        aria-valuenow={score}
        aria-valuemin={min}
        aria-valuemax={max}
        sx={{ mb: 0.5 }}
      >
        <Stack direction="row" sx={{ height: 5, borderRadius: 2.5, overflow: 'hidden' }}>
          {bands.map((b, i) => {
            const width = ((b.to - b.from) / (max - min)) * 100;
            const isActive = score !== undefined && score >= b.from && score <= b.to;
            return (
              <Box key={i} sx={{
                width: `${width}%`,
                bgcolor: isActive ? b.color : alpha(b.color, 0.18),
                transition: 'background-color 300ms ease',
              }} />
            );
          })}
        </Stack>
        <Box sx={{ position: 'relative', height: 10, mt: 0.25 }}>
          <Box sx={{
            position: 'absolute',
            left: `calc(${pct}% - 4px)`,
            top: -8,
            width: 8, height: 8,
            borderRadius: '50%',
            bgcolor: accent,
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: `0 0 0 1px ${alpha(accent, 0.5)}`,
          }} />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" sx={{ ...NUMERIC_SX, fontSize: '0.72rem', color: 'text.secondary' }}>{min}</Typography>
            <Typography variant="caption" sx={{ ...NUMERIC_SX, fontSize: '0.72rem', color: 'text.secondary' }}>{max}</Typography>
          </Stack>
        </Box>
      </Box>

      {factors.length > 0 && (
        <Box mt={1.25} pt={1.25} sx={{ borderTop: `1px dashed ${alpha(accent, 0.25)}` }}>
          <Typography variant="caption" sx={{ ...SECTION_LABEL_SX, display: 'block', mb: 0.625 }}>
            Key Factors
          </Typography>
          <Stack spacing={0.5}>
            {factors.slice(0, 4).map((f, i) => (
              <Stack key={i} direction="row" spacing={0.75} alignItems="flex-start">
                <Box sx={{
                  ...NUMERIC_SX,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: accent,
                  bgcolor: alpha(accent, 0.14),
                  px: 0.5, py: 0.125,
                  borderRadius: 0.5,
                  minWidth: 18,
                  textAlign: 'center',
                  lineHeight: 1.4,
                  flexShrink: 0,
                }}>
                  {f.importance ?? i + 1}
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5, fontSize: '0.75rem' }}>
                  {f.description}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

const verdictColorMap = (theme: Theme): Record<string, string> => ({
  APPROVE:     theme.palette.success.main,
  CONDITIONAL: theme.palette.warning.main,
  REFER:       theme.palette.info.main,
  DECLINE:     theme.palette.error.main,
});

const RiskSummary = ({ data }: { data: Record<string, any> }) => {
  const theme = useTheme();
  const verdict: string | undefined = typeof data.verdict === 'string'
    ? data.verdict.toUpperCase().trim()
    : undefined;
  const tier: number | null | undefined = typeof data.tier === 'number' ? data.tier : null;
  const conditions: string[] = Array.isArray(data.conditions) ? data.conditions : [];

  const verdictClr = verdict ? verdictColorMap(theme)[verdict] : undefined;
  const riskClr = riskColorMap(theme)[data.riskLevel] || theme.palette.warning.main;
  const color = verdictClr || riskClr;

  const positive: string[] = Array.isArray(data.positiveFactors) ? data.positiveFactors : [];
  const negative: string[] = Array.isArray(data.negativeFactors) ? data.negativeFactors : [];
  const riskLabel = typeof data.riskLevel === 'string'
    ? data.riskLevel.replace(/[_-]+/g, ' ')
    : undefined;

  const verdictLabel = verdict
    ? `${verdict}${tier ? ` · TIER ${tier}` : ''}`
    : undefined;

  return (
    <Box sx={WIDGET_CARD_SX(alpha(color, 0.25), alpha(color, 0.04))}>
      <WidgetHeader
        icon={<WarningOutlined />}
        title="Risk Assessment"
        badge={verdictLabel || riskLabel}
        badgeColor={color}
      />

      {verdictLabel && riskLabel && (
        <Typography variant="caption" sx={{
          display: 'block',
          mt: -0.5,
          mb: 1,
          color: 'text.secondary',
          fontSize: '0.7rem',
          letterSpacing: 0.4,
          textTransform: 'uppercase',
        }}>
          Risk: {riskLabel}
        </Typography>
      )}

      {positive.length > 0 && (
        <Box mb={negative.length > 0 || conditions.length > 0 ? 1 : 0.5}>
          <Typography variant="caption" sx={{ ...SECTION_LABEL_SX, display: 'block', mb: 0.5 }}>
            Positive
          </Typography>
          <Stack spacing={0.5}>
            {positive.map((f, i) => (
              <Stack key={i} direction="row" spacing={0.75} alignItems="flex-start">
                <CheckCircleOutlined style={{
                  fontSize: 11,
                  color: theme.palette.success.main,
                  marginTop: 4,
                  flexShrink: 0,
                }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.55, fontSize: '0.75rem' }}>
                  {f}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {negative.length > 0 && (
        <Box mb={conditions.length > 0 || data.recommendation ? 1 : 0.5}>
          <Typography variant="caption" sx={{ ...SECTION_LABEL_SX, display: 'block', mb: 0.5 }}>
            Concerns
          </Typography>
          <Stack spacing={0.5}>
            {negative.map((f, i) => (
              <Stack key={i} direction="row" spacing={0.75} alignItems="flex-start">
                <WarningOutlined style={{
                  fontSize: 11,
                  color: theme.palette.error.main,
                  marginTop: 4,
                  flexShrink: 0,
                }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.55, fontSize: '0.75rem' }}>
                  {f}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {conditions.length > 0 && (
        <Box mb={data.recommendation ? 1 : 0.5}>
          <Typography variant="caption" sx={{
            ...SECTION_LABEL_SX,
            display: 'block',
            mb: 0.5,
            color: theme.palette.warning.main,
          }}>
            Conditions to fund
          </Typography>
          <Stack spacing={0.5}>
            {conditions.map((c, i) => (
              <Stack key={i} direction="row" spacing={0.75} alignItems="flex-start">
                <Box sx={{
                  width: 16,
                  height: 16,
                  borderRadius: 0.5,
                  bgcolor: alpha(theme.palette.warning.main, 0.18),
                  color: theme.palette.warning.dark,
                  fontFamily: 'SF Mono, Menlo, monospace',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  mt: 0.25,
                }}>
                  {i + 1}
                </Box>
                <Typography variant="caption" sx={{
                  color: 'text.primary',
                  lineHeight: 1.55,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}>
                  {c}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {data.recommendation && (
        <Box sx={{
          mt: 0.75,
          p: 1.25,
          borderRadius: 1.25,
          bgcolor: alpha(color, 0.08),
          border: `1px solid ${alpha(color, 0.2)}`,
          borderLeft: `3px solid ${color}`,
        }}>
          <Typography variant="caption" sx={{
            ...SECTION_LABEL_SX,
            color,
            display: 'block',
            mb: 0.375,
          }}>
            {verdict ? 'Rationale' : 'Recommendation'}
          </Typography>
          <Typography variant="caption" sx={{
            color: 'text.primary',
            lineHeight: 1.6,
            fontSize: '0.8125rem',
            fontWeight: 500,
          }}>
            {data.recommendation}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const DebtBreakdown = ({ data }: { data: Record<string, any> }) => {
  const theme = useTheme();
  const installment: number = data.installmentBalance || 0;
  const revolving: number = data.revolvingBalance || 0;
  const realEstate: number = data.realEstateBalance || 0;
  const total = installment + revolving + realEstate;
  const monthlyPayment: number | undefined = data.totalMonthlyPayment;
  const util: number | undefined = data.revolvingUtilization;

  const rows = [
    { label: 'Installment', value: installment, color: theme.palette.primary.main },
    { label: 'Revolving', value: revolving, color: theme.palette.warning.main },
    { label: 'Real Estate', value: realEstate, color: theme.palette.info.main },
  ].filter(r => r.value > 0);

  const utilClr = utilizationColor(util, theme);

  return (
    <Box sx={WIDGET_CARD_SX(theme.palette.divider, alpha(theme.palette.primary.main, 0.03))}>
      <WidgetHeader icon={<BarChartOutlined />} title="Debt Profile" />

      <Stack direction="row" alignItems="baseline" spacing={1} mb={1.25}>
        <Tooltip title={formatMoneyFull(total)} arrow placement="top">
          <Typography sx={{
            ...NUMERIC_SX,
            fontSize: '1.625rem',
            fontWeight: 800,
            color: 'text.primary',
            lineHeight: 1,
            letterSpacing: -0.8,
            cursor: 'help',
          }}>
            {formatMoney(total, true)}
          </Typography>
        </Tooltip>
        <Typography variant="caption" sx={{ ...SECTION_LABEL_SX }}>
          total owed
        </Typography>
      </Stack>

      {rows.length > 0 && (
        <Box mb={1.25}>
          <SegmentedBar
            ariaLabel="Debt composition by type"
            segments={rows.map(r => ({ value: r.value, color: r.color, label: r.label }))}
            height={6}
          />
          <Stack spacing={0.375} mt={1}>
            {rows.map((r, i) => (
              <Box
                key={i}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  columnGap: 1,
                  alignItems: 'center',
                }}
              >
                <Stack direction="row" spacing={0.625} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: r.color }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    {r.label}
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{
                  ...NUMERIC_SX,
                  color: 'text.secondary',
                  fontSize: '0.76rem',
                  textAlign: 'right',
                }}>
                  {total > 0 ? `${((r.value / total) * 100).toFixed(0)}%` : ''}
                </Typography>
                <Tooltip title={formatMoneyFull(r.value)} arrow placement="left">
                  <Typography sx={{
                    ...NUMERIC_SX,
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                    color: 'text.primary',
                    textAlign: 'right',
                    minWidth: 68,
                    cursor: 'help',
                  }}>
                    {formatMoney(r.value, true)}
                  </Typography>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <Divider sx={{ my: 1 }} />

      <Stack spacing={0.5}>
        {monthlyPayment !== undefined && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              Monthly Payment
            </Typography>
            <Typography sx={{
              ...NUMERIC_SX,
              fontWeight: 700,
              fontSize: '0.8125rem',
              color: 'text.primary',
            }}>
              {formatMoney(monthlyPayment)}
            </Typography>
          </Stack>
        )}
        {util !== undefined && (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.375}>
              <Stack direction="row" spacing={0.625} alignItems="center">
                <ThresholdDot color={utilClr} />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  Revolving Utilization
                </Typography>
              </Stack>
              <Typography sx={{
                ...NUMERIC_SX,
                fontWeight: 700,
                fontSize: '0.8125rem',
                color: utilClr,
              }}>
                {formatPercent(util, 0)}
              </Typography>
            </Stack>
            <Box
              role="progressbar"
              aria-label={`Revolving utilization ${util}%`}
              aria-valuenow={util}
              aria-valuemin={0}
              aria-valuemax={100}
              sx={{
                position: 'relative',
                height: 4,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.text.primary, 0.08),
                overflow: 'hidden',
              }}
            >
              <Box sx={{
                position: 'absolute',
                left: '30%', top: 0, bottom: 0,
                width: '1px',
                bgcolor: alpha(theme.palette.text.primary, 0.25),
              }} />
              <Box sx={{
                position: 'absolute',
                left: '60%', top: 0, bottom: 0,
                width: '1px',
                bgcolor: alpha(theme.palette.text.primary, 0.25),
              }} />
              <Box sx={{
                height: '100%',
                width: `${Math.min(util, 100)}%`,
                bgcolor: utilClr,
                borderRadius: 2,
                transition: 'width 400ms ease',
              }} />
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

const PaymentHistoryChart = ({ data }: { data: Record<string, any> }) => {
  const theme = useTheme();
  const onTimePct: number = data.onTimePercent ?? 0;
  const months: number = data.monthsAnalyzed ?? 0;
  const late30: number = data.late30Count ?? 0;
  const late60: number = data.late60Count ?? 0;
  const late90: number = data.late90PlusCount ?? 0;
  const derog: number = data.derogatoryCount ?? 0;

  const latePct = Math.max(0, 100 - onTimePct);
  const totalLate = late30 + late60 + late90 + derog;
  const lateWeight = (count: number) => (totalLate > 0 ? (count / totalLate) * latePct : 0);
  const color = onTimeColor(onTimePct, theme);

  const segments = [
    { value: onTimePct, color: theme.palette.success.main, label: `On-time ${formatPercent(onTimePct)}` },
    { value: lateWeight(late30), color: theme.palette.warning.main, label: `30d late · ${late30}` },
    { value: lateWeight(late60), color: theme.palette.warning.dark, label: `60d late · ${late60}` },
    { value: lateWeight(late90), color: theme.palette.error.main, label: `90d+ late · ${late90}` },
    { value: lateWeight(derog), color: theme.palette.error.dark, label: `Derogatory · ${derog}` },
  ];

  const tiles = [
    { label: '30d', value: late30, color: theme.palette.warning.main },
    { label: '60d', value: late60, color: theme.palette.warning.dark },
    { label: '90d+', value: late90, color: theme.palette.error.main },
    { label: 'Derog', value: derog, color: theme.palette.error.dark },
  ];

  return (
    <Box sx={WIDGET_CARD_SX(alpha(color, 0.2), alpha(color, 0.03))}>
      <WidgetHeader
        icon={<ClockCircleOutlined />}
        title="Payment History"
        badge={months > 0 ? `${months} mo` : undefined}
      />

      <Stack direction="row" alignItems="baseline" spacing={1} mb={1}>
        <Typography sx={{
          ...NUMERIC_SX,
          fontSize: '1.625rem',
          fontWeight: 800,
          color,
          lineHeight: 1,
          letterSpacing: -0.6,
        }}>
          {formatPercent(onTimePct)}
        </Typography>
        <Typography variant="caption" sx={{ ...SECTION_LABEL_SX }}>
          on-time
        </Typography>
      </Stack>

      <Box mb={1.25}>
        <SegmentedBar
          ariaLabel="Payment composition"
          segments={segments}
          height={7}
        />
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 0.75,
      }}>
        {tiles.map((t, i) => {
          const active = t.value > 0;
          return (
            <Box
              key={i}
              sx={{
                textAlign: 'center',
                py: 0.75, px: 0.25,
                borderRadius: 1,
                bgcolor: active ? alpha(t.color, 0.12) : alpha(theme.palette.text.primary, 0.04),
                border: `1px solid ${active ? alpha(t.color, 0.25) : 'transparent'}`,
              }}
            >
              <Typography sx={{
                ...NUMERIC_SX,
                fontSize: '1rem',
                fontWeight: 700,
                color: active ? t.color : 'text.secondary',
                lineHeight: 1.1,
              }}>
                {formatCount(t.value)}
              </Typography>
              <Typography variant="caption" sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                color: 'text.primary',
                opacity: 0.78,
                display: 'block',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                mt: 0.125,
              }}>
                {t.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const TradelineSummary = ({ data }: { data: Record<string, any> }) => {
  const theme = useTheme();
  const total: number = data.totalAccounts ?? 0;
  const open: number = data.openAccounts ?? 0;
  const derog: number = data.derogatory ?? 0;
  const oldest: string | undefined = data.oldestAccountDate;
  const inquiries: number = data.recentInquiries6Mo ?? 0;
  const accountsByType: Record<string, number> | undefined = data.accountsByType;
  const closed = Math.max(0, total - open);

  const inqClr = inquiryColor(inquiries, theme);
  const derogClr = derogatoryColor(derog, theme);

  const typeEntries = accountsByType && typeof accountsByType === 'object'
    ? Object.entries(accountsByType).filter(([, v]) => typeof v === 'number' && v > 0)
    : [];
  const maxTypeCount = typeEntries.reduce((m, [, v]) => Math.max(m, v as number), 0);

  return (
    <Box sx={WIDGET_CARD_SX(theme.palette.divider, alpha(theme.palette.info.main, 0.03))}>
      <WidgetHeader icon={<FundOutlined />} title="Tradeline Summary" />

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 0.75,
        mb: 1.25,
      }}>
        <Box sx={{
          p: 0.875,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.text.primary, 0.04),
          textAlign: 'center',
        }}>
          <Typography sx={{
            ...NUMERIC_SX,
            fontSize: '1.25rem',
            fontWeight: 800,
            color: 'text.primary',
            lineHeight: 1.1,
          }}>
            {formatCount(total)}
          </Typography>
          <Typography variant="caption" sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                color: 'text.primary',
                opacity: 0.78,
            display: 'block',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>
            Accounts
          </Typography>
        </Box>
        <Box sx={{
          p: 0.875,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.success.main, open > 0 ? 0.1 : 0.04),
          textAlign: 'center',
          border: `1px solid ${open > 0 ? alpha(theme.palette.success.main, 0.2) : 'transparent'}`,
        }}>
          <Typography sx={{
            ...NUMERIC_SX,
            fontSize: '1.25rem',
            fontWeight: 800,
            color: open > 0 ? theme.palette.success.main : 'text.secondary',
            lineHeight: 1.1,
          }}>
            {formatCount(open)}
          </Typography>
          <Typography variant="caption" sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                color: 'text.primary',
                opacity: 0.78,
            display: 'block',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>
            Open
          </Typography>
        </Box>
        <Box sx={{
          p: 0.875,
          borderRadius: 1,
          bgcolor: alpha(derogClr, derog > 0 ? 0.12 : 0.04),
          textAlign: 'center',
          border: `1px solid ${derog > 0 ? alpha(derogClr, 0.25) : 'transparent'}`,
        }}>
          <Typography sx={{
            ...NUMERIC_SX,
            fontSize: '1.25rem',
            fontWeight: 800,
            color: derog > 0 ? derogClr : 'text.secondary',
            lineHeight: 1.1,
          }}>
            {formatCount(derog)}
          </Typography>
          <Typography variant="caption" sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                color: 'text.primary',
                opacity: 0.78,
            display: 'block',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>
            Derog
          </Typography>
        </Box>
      </Box>

      {typeEntries.length > 0 && (
        <Box mb={1}>
          <Typography variant="caption" sx={{ ...SECTION_LABEL_SX, display: 'block', mb: 0.625 }}>
            By Type
          </Typography>
          <Stack spacing={0.5}>
            {typeEntries.map(([type, countRaw]) => {
              const count = countRaw as number;
              return (
                <Box
                  key={type}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '88px 1fr 28px',
                    columnGap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="caption" sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    textTransform: 'capitalize',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {type.replace(/_/g, ' ')}
                  </Typography>
                  <Box sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    overflow: 'hidden',
                  }}>
                    <Box sx={{
                      height: '100%',
                      width: maxTypeCount > 0 ? `${(count / maxTypeCount) * 100}%` : 0,
                      bgcolor: theme.palette.primary.main,
                      borderRadius: 2,
                    }} />
                  </Box>
                  <Typography sx={{
                    ...NUMERIC_SX,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: 'text.primary',
                    textAlign: 'right',
                  }}>
                    {count}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      <Divider sx={{ my: 1 }} />

      <Stack spacing={0.5}>
        {closed > 0 && (
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              Closed
            </Typography>
            <Typography sx={{ ...NUMERIC_SX, fontSize: '0.75rem', fontWeight: 700, color: 'text.primary' }}>
              {formatCount(closed)}
            </Typography>
          </Stack>
        )}
        {oldest && (
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              Oldest Account
            </Typography>
            <Typography sx={{ ...NUMERIC_SX, fontSize: '0.75rem', fontWeight: 700, color: 'text.primary' }}>
              {formatAccountAge(oldest)}
            </Typography>
          </Stack>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={0.625} alignItems="center">
            <ThresholdDot color={inqClr} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              Inquiries (6 mo)
            </Typography>
          </Stack>
          <Typography sx={{ ...NUMERIC_SX, fontSize: '0.75rem', fontWeight: 700, color: inqClr }}>
            {formatCount(inquiries)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

const ProfileSummary = ({ data }: { data: Record<string, any> }) => {
  const theme = useTheme();
  const rows: Array<{ label: string; value: React.ReactNode; color?: string }> = [
    { label: 'Total Accounts', value: `${data.totalAccounts ?? 0} · ${data.openAccounts ?? 0} open` },
    { label: 'Satisfactory', value: formatCount(data.satisfactory), color: theme.palette.success.main },
    { label: 'Derogatory', value: formatCount(data.derogatory), color: data.derogatory > 0 ? theme.palette.error.main : undefined },
    { label: 'Now Delinquent', value: formatCount(data.nowDelinquent), color: data.nowDelinquent > 0 ? theme.palette.error.main : undefined },
    { label: 'Was Delinquent', value: formatCount(data.wasDelinquent), color: data.wasDelinquent > 0 ? theme.palette.warning.main : undefined },
    { label: 'Oldest Trade', value: formatAccountAge(data.oldestTrade) },
    { label: 'Public Records', value: formatCount(data.publicRecords), color: data.publicRecords > 0 ? theme.palette.error.main : undefined },
    { label: 'Disputed', value: formatCount(data.disputedAccounts) },
  ];

  return (
    <Box sx={WIDGET_CARD_SX(theme.palette.divider, alpha(theme.palette.info.main, 0.03))}>
      <WidgetHeader icon={<FundOutlined />} title="Profile Summary" badge={`${data.totalAccounts ?? 0}`} />
      <Stack spacing={0.625}>
        {rows.map((row, i) => (
          <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {row.label}
            </Typography>
            <Typography sx={{
              ...NUMERIC_SX,
              fontWeight: 700,
              fontSize: '0.8125rem',
              color: row.color || 'text.primary',
            }}>
              {row.value ?? 0}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const HighUtilization = ({ data }: { data: Record<string, any> }) => {
  const theme = useTheme();
  const accounts: Array<any> = Array.isArray(data.accounts) ? data.accounts : [];

  return (
    <Box sx={WIDGET_CARD_SX(alpha(theme.palette.error.main, 0.25), alpha(theme.palette.error.main, 0.04))}>
      <WidgetHeader
        icon={<WarningOutlined />}
        title="High Utilization"
        badge={`${accounts.length}`}
        badgeColor={theme.palette.error.main}
      />
      <Stack spacing={1}>
        {accounts.map((a, i) => {
          const utilClr = a.utilization >= 90 ? theme.palette.error.main : theme.palette.warning.main;
          return (
            <Box key={i}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Stack direction="row" spacing={0.625} alignItems="center">
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.primary' }}>
                    {a.type}
                  </Typography>
                  <Chip label={a.status} size="small" sx={{
                    height: 16, fontSize: '0.72rem', fontWeight: 600,
                    bgcolor: a.status === 'Current'
                      ? alpha(theme.palette.success.main, 0.12)
                      : alpha(theme.palette.error.main, 0.12),
                    color: a.status === 'Current' ? theme.palette.success.main : theme.palette.error.main,
                    '& .MuiChip-label': { px: 0.5 },
                  }} />
                </Stack>
                <Typography sx={{
                  ...NUMERIC_SX,
                  fontWeight: 800,
                  fontSize: '0.875rem',
                  color: utilClr,
                }}>
                  {formatPercent(a.utilization, 0)}
                </Typography>
              </Stack>
              <Box sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.error.main, 0.08),
                overflow: 'hidden',
                mb: 0.375,
              }}>
                <Box sx={{
                  height: '100%',
                  width: `${Math.min(a.utilization, 100)}%`,
                  bgcolor: utilClr,
                  borderRadius: 2,
                }} />
              </Box>
              <Typography variant="caption" sx={{
                ...NUMERIC_SX,
                color: 'text.secondary',
                fontSize: '0.76rem',
              }}>
                {formatMoney(a.balance)} <Box component="span" sx={{ color: 'text.secondary', fontFamily: 'inherit' }}>of</Box> {formatMoney(a.limit)}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

const InquiryAnalysis = ({ data }: { data: Record<string, any> }) => {
  const theme = useTheme();
  const breakdown: Array<any> = Array.isArray(data.breakdown) ? data.breakdown : [];
  const inq6mo: number = data.inquiries6mo ?? 0;
  const total: number = data.totalInquiries ?? 0;
  const color = inquiryColor(inq6mo, theme);

  return (
    <Box sx={WIDGET_CARD_SX(
      data.stackingSignal ? alpha(theme.palette.warning.main, 0.3) : theme.palette.divider,
      data.stackingSignal ? alpha(theme.palette.warning.main, 0.04) : alpha(theme.palette.info.main, 0.02),
    )}>
      <WidgetHeader
        icon={<SearchOutlined />}
        title="Inquiry Analysis"
        badge={`${inq6mo} / 6 mo`}
        badgeColor={color}
      />

      <Stack direction="row" spacing={2} alignItems="baseline" mb={1.25}>
        <Box>
          <Typography sx={{
            ...NUMERIC_SX,
            fontSize: '1.5rem',
            fontWeight: 800,
            color,
            lineHeight: 1,
          }}>
            {formatCount(inq6mo)}
          </Typography>
          <Typography variant="caption" sx={{ ...SECTION_LABEL_SX, display: 'block', mt: 0.375 }}>
            Last 6 mo
          </Typography>
        </Box>
        <Box>
          <Typography sx={{
            ...NUMERIC_SX,
            fontSize: '1.25rem',
            fontWeight: 700,
            color: 'text.secondary',
            lineHeight: 1,
          }}>
            {formatCount(total)}
          </Typography>
          <Typography variant="caption" sx={{ ...SECTION_LABEL_SX, display: 'block', mt: 0.375 }}>
            Total
          </Typography>
        </Box>
      </Stack>

      {breakdown.length > 0 && (
        <Stack spacing={0.5}>
          {breakdown.map((item, i) => (
            <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                {item.type}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography sx={{
                  ...NUMERIC_SX,
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  color: item.count >= 3 ? theme.palette.warning.main : 'text.primary',
                }}>
                  {formatCount(item.count)}
                </Typography>
                {item.count >= 3 && (
                  <WarningOutlined style={{ fontSize: 10, color: theme.palette.warning.main }} />
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      {data.stackingSignal && (
        <Box sx={{
          mt: 1.25,
          px: 1,
          py: 0.625,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.warning.main, 0.1),
          border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
          borderLeft: `3px solid ${theme.palette.warning.main}`,
        }}>
          <Typography variant="caption" sx={{
            color: theme.palette.warning.main,
            fontWeight: 700,
            fontSize: '0.7rem',
            lineHeight: 1.5,
          }}>
            Stacking signal — multiple consumer finance inquiries detected
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const RelationshipSignals = ({ data }: { data: Record<string, any> }) => {
  const theme = useTheme();
  const samePerson: Array<number | string> = Array.isArray(data.samePersonOtherApplications)
    ? data.samePersonOtherApplications : [];
  const otherOwners: Array<string> = Array.isArray(data.otherOwnersAtAddress)
    ? data.otherOwnersAtAddress : [];
  const notes: string = typeof data.notes === 'string' ? data.notes : '';
  const hasAny = samePerson.length > 0 || otherOwners.length > 0;
  const accent = hasAny ? theme.palette.warning.main : theme.palette.success.main;

  return (
    <Box sx={WIDGET_CARD_SX(
      hasAny ? alpha(accent, 0.25) : theme.palette.divider,
      hasAny ? alpha(accent, 0.04) : alpha(theme.palette.info.main, 0.02),
    )}>
      <WidgetHeader
        icon={<TeamOutlined />}
        title="Relationship Signals"
        badge={hasAny ? 'match found' : 'clean'}
        badgeColor={accent}
      />

      {!hasAny && (
        <Stack direction="row" spacing={0.875} alignItems="center" sx={{ py: 0.5 }}>
          <CheckCircleOutlined style={{ fontSize: 14, color: accent }} />
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8125rem', lineHeight: 1.5 }}>
            No related applications or shared-address matches found.
          </Typography>
        </Stack>
      )}

      {samePerson.length > 0 && (
        <Box mb={otherOwners.length > 0 ? 1.25 : 0.5}>
          <Stack direction="row" alignItems="baseline" spacing={0.875} mb={0.625}>
            <Typography sx={{
              ...NUMERIC_SX,
              fontSize: '1.25rem',
              fontWeight: 800,
              color: accent,
              lineHeight: 1,
            }}>
              {formatCount(samePerson.length)}
            </Typography>
            <Typography variant="caption" sx={{ ...SECTION_LABEL_SX }}>
              other applications · same person
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {samePerson.map((app, i) => (
              <Chip
                key={i}
                label={`#${app}`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  ...NUMERIC_SX,
                  bgcolor: alpha(accent, 0.14),
                  color: accent,
                  border: `1px solid ${alpha(accent, 0.3)}`,
                  '& .MuiChip-label': { px: 0.875 },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {otherOwners.length > 0 && (
        <Box>
          <Stack direction="row" alignItems="baseline" spacing={0.875} mb={0.625}>
            <Typography sx={{
              ...NUMERIC_SX,
              fontSize: '1.25rem',
              fontWeight: 800,
              color: accent,
              lineHeight: 1,
            }}>
              {formatCount(otherOwners.length)}
            </Typography>
            <Typography variant="caption" sx={{ ...SECTION_LABEL_SX }}>
              other owners · same address
            </Typography>
          </Stack>
          <Stack spacing={0.375}>
            {otherOwners.slice(0, 4).map((hash, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={0.75}>
                <Box sx={{
                  width: 4, height: 4, borderRadius: '50%',
                  bgcolor: alpha(accent, 0.5),
                  flexShrink: 0,
                }} />
                <Typography variant="caption" sx={{
                  ...NUMERIC_SX,
                  fontSize: '0.7rem',
                  color: 'text.secondary',
                }}>
                  {typeof hash === 'string' ? `${hash.slice(0, 16)}…` : String(hash)}
                </Typography>
              </Stack>
            ))}
            {otherOwners.length > 4 && (
              <Typography variant="caption" sx={{
                fontSize: '0.74rem',
                color: 'text.secondary',
                fontStyle: 'italic',
                pl: 1.125,
              }}>
                +{otherOwners.length - 4} more
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      {notes && (
        <>
          {hasAny && <Divider sx={{ my: 1, borderColor: alpha(theme.palette.divider, 0.5) }} />}
          <Typography variant="caption" sx={{
            color: 'text.secondary',
            lineHeight: 1.55,
            fontSize: '0.75rem',
            fontStyle: hasAny ? 'normal' : 'italic',
          }}>
            {notes}
          </Typography>
        </>
      )}
    </Box>
  );
};

const renderWidgetBody = (widget: ChatCardWidget): React.ReactNode => {
  if (!isKnownAnalysisWidget(widget)) {
    return (
      <WidgetFallback
        widget={widget}
        reason={`Unknown widget type "${widget?.type ?? 'unknown'}"`}
      />
    );
  }
  switch (widget.type) {
    case 'score_card': return <ScoreCard data={widget.data} />;
    case 'risk_summary': return <RiskSummary data={widget.data} />;
    case 'debt_breakdown': return <DebtBreakdown data={widget.data} />;
    case 'payment_history_chart': return <PaymentHistoryChart data={widget.data} />;
    case 'tradeline_summary': return <TradelineSummary data={widget.data} />;
    case 'relationship_signals': return <RelationshipSignals data={widget.data} />;
    case 'profile_summary': return <ProfileSummary data={widget.data} />;
    case 'high_utilization': return <HighUtilization data={widget.data} />;
    case 'inquiry_analysis': return <InquiryAnalysis data={widget.data} />;
    default: {
      // Exhaustiveness check: AnalysisWidget is a closed union, so this is unreachable.
      // If we add a new widget type, TypeScript flags this branch.
      const _exhaustive: never = widget;
      return null;
    }
  }
};

const AiChatWidgetCard = ({ widget }: AiChatWidgetCardProps) => (
  <WidgetErrorBoundary widget={widget}>
    {renderWidgetBody(widget)}
  </WidgetErrorBoundary>
);

export default AiChatWidgetCard;
